import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ListItemCardCst from "@components/ListItemCardCst";
import { useCardActions } from "@hooks/useCardActions";
import { classroomEditionKind } from "@utils/cardActions";
import { editionPerm, usePermissions } from "@utils/permissions";
import { CatalogEntityType } from "../types";
import { mapEntityToCard } from "../mapEntityToCard";
import { openEnrollmentModal } from "../Enrollment/host";
import {
  clearPendingEnrollment,
  usePendingCounters,
} from "../Enrollment/pendingCountersStore";
import { openAssociateInitiativesModal } from "../AssociateInitiatives/host";
import { openUsersAndGroupsModal } from "../UsersAndGroups/host";
import { UsersAndGroupsTab } from "../UsersAndGroups/types";
import { ActivityV1Type } from "../AddActivity/types";

const PENDING_REFRESH_INTERVAL_MS = 10 * 1000;

interface CatalogItemProps {
  entity: any;
  entityType: CatalogEntityType;
  activityType?: ActivityV1Type;
  selected?: boolean;
  onSelect?: (next: boolean) => void;
  onDetails: (entity: any) => void;
  onPublish?: (entity: any) => void;

  onEdit?: (entity: any) => void;
  onDelete?: (entity: any) => void;
  onEnrolled?: () => void;
  showDetails?: boolean;
}

const CatalogItem: React.FC<CatalogItemProps> = ({
  entity,
  entityType,
  activityType,
  selected,
  onSelect,
  onDetails,
  onPublish,
  onEdit,
  onEnrolled,
  onDelete,
  showDetails,
}) => {
  const { t } = useTranslation("catalog-v2");
  const { t: tActivity } = useTranslation("wizard-activity");
  const { id: corporateId } = useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const editionKind = classroomEditionKind(entity);

  const overrides = useMemo(() => {
    const base: Record<string, () => void> = {
      enroll: () =>
        corporateId &&
        openEnrollmentModal({
          objectType: entityType,
          objectId: entity.id,
          objectTitle: entity.title || entity.name || undefined,
          corporateId,
          isEcm: !!(entity.ecm_specialization || entity.toj_specialization),
          currentCounters: entity.metadata_counters,
          onSuccess: onEnrolled,
        }),
      "associate-initiatives": () =>
        corporateId &&
        openAssociateInitiativesModal({
          objectType: entityType,
          objectId: entity.id,
          objectTitle: entity.title || entity.name || undefined,
          corporateId,
          initialAssociatedIds: Array.isArray(entity.initiative)
            ? entity.initiative
                .map((i: any) => (typeof i === "object" ? i?.id : i))
                .filter((id: any): id is number => typeof id === "number")
            : [],
          onSuccess: onEnrolled,
        }),
    };
    const openUsersGroups = (initialTab: UsersAndGroupsTab) =>
      corporateId &&
      openUsersAndGroupsModal({
        initialTab,
        objectType: entityType,
        objectId: entity.id,
        objectTitle: entity.title || entity.name || undefined,
        corporateId,
        activityType: activityType,
        isStandalone: entity.is_standalone,
        onSuccess: onEnrolled,
      });

    base["enrolled-users"] = () => openUsersGroups("enrolled-users");
    base["pre-enrolled-users"] = () => openUsersGroups("pre-enrolled-users");
    base["waiting-users"] = () => openUsersGroups("waiting-users");
    base["enrolled-groups"] = () => openUsersGroups("enrolled-groups");

    base["see-editions"] = () => {
      if (!corporateId) return;
      navigate(
        `/v2/corporate/${corporateId}/detail/catalog/activities/${entity.id}/editions?kind=${editionKind}`,
      );
    };

    base["duplicate"] = () => {
      if (!corporateId) return;
      const segment =
        entityType === "activity"
          ? "activities"
          : entityType === "course"
            ? "courses"
            : "paths";
      navigate(
        `/v2/corporate/${corporateId}/detail/catalog/${segment}/${entity.id}/duplicate`,
      );
    };
    base["delete"] = () => {
      if (!corporateId) return;
      console.info("before ondelete", entity);
      onDelete?.(entity);
    };
    if (onEdit) base.edit = () => onEdit(entity);
    return base;
  }, [
    onEdit,
    entity,
    entityType,
    editionKind,
    corporateId,
    navigate,
    onEnrolled,
  ]);

  const moreActions = useCardActions(entityType, entity, {
    corporateId,
    overrides,
    testIdPrefix: "catalog-card-actions",
  });

  const pendingCounters = usePendingCounters(corporateId, entity.id);
  const beUsers = Number(entity.metadata_counters?.enrolled_users ?? 0);
  const beGroups = Number(entity.metadata_counters?.enrolled_groups ?? 0);
  const isPendingAligned =
    !!pendingCounters &&
    beUsers >= pendingCounters.enrolledUsers &&
    beGroups >= pendingCounters.enrolledGroups;

  useEffect(() => {
    if (!pendingCounters || !corporateId) return;
    if (isPendingAligned) {
      clearPendingEnrollment(corporateId, entity.id);
      return;
    }
    const interval = setInterval(
      () => onEnrolled?.(),
      PENDING_REFRESH_INTERVAL_MS,
    );
    return () => clearInterval(interval);
  }, [pendingCounters, isPendingAligned, corporateId, entity.id, onEnrolled]);

  const displayedEntity = useMemo(
    () =>
      pendingCounters && !isPendingAligned
        ? {
            ...entity,
            metadata_counters: {
              ...entity.metadata_counters,
              enrolled_users: Math.max(beUsers, pendingCounters.enrolledUsers),
              enrolled_groups: Math.max(
                beGroups,
                pendingCounters.enrolledGroups,
              ),
            },
          }
        : entity,
    [entity, pendingCounters, isPendingAligned, beUsers, beGroups],
  );

  const cardProps = mapEntityToCard(displayedEntity, entityType, {
    t,
    tActivity,
    onDetails,
    onPublish: onPublish ? () => onPublish(entity) : undefined,
    onCreateEdition:
      corporateId && can(editionPerm(editionKind, "create"))
        ? (e) =>
            navigate(
              `/v2/corporate/${corporateId}/detail/catalog/activities/${e.id}/editions/add?kind=${editionKind}`,
            )
        : undefined,
    onSeeEditions:
      corporateId && can(editionPerm(editionKind, "retrieve"))
        ? overrides["see-editions"]
        : undefined,
  });

  // Il pulsante "Dettagli" e' nascosto nel catalogo principale ma mostrato
  // dove `showDetails` e' attivo (es. dashboard "Ultime offerte formative").
  // Il bottone resta definito in mapEntityToCard.
  const resolvedCardProps = showDetails
    ? cardProps
    : { ...cardProps, actions: [] };

  return (
    <ListItemCardCst
      {...resolvedCardProps}
      selectable={selected !== undefined}
      selected={selected}
      onSelect={onSelect}
      moreActions={moreActions}
      moreActionsTestId={`catalog-card-${entity.id}-more-actions-button`}
      activityType={activityType}
    />
  );
};

export default CatalogItem;
